import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { FileUpload } from 'primereact/fileupload';
import { Badge } from 'primereact/badge';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ProgressBar } from 'primereact/progressbar';
import { importControlAPI } from '../services/importControlAPI';
import FileErrorModal from '../components/FileErrorModal'; 

const MissingLayoutsUploadPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const toast = useRef(null);
    const fileUploadRef = useRef({});

    const [layouts, setLayouts] = useState([]);
    const [allFilesSelected, setAllFilesSelected] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
    const [totalUploads, setTotalUploads] = useState(0);
    const [currentFileProgress, setCurrentFileProgress] = useState(0);
    const [overallProgress, setOverallProgress] = useState(0);
    const [tokenValid, setTokenValid] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploadCompleted, setUploadCompleted] = useState(false);
    const token = searchParams.get('token');

    // <--- 2. NOVOS ESTADOS PARA O MODAL DE ERRO DE ARQUIVO
    const [showFileErrorModal, setShowFileErrorModal] = useState(false);
    const [fileErrorMessage, setFileErrorMessage] = useState('');

    const formatMonthYear = (dateStr) => {
        if (!dateStr) return '';
        const [year, month] = dateStr.split('-');
        const months = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
        return `${months[parseInt(month) - 1]} - ${year}`;
    };

    useEffect(() => {
        if (token) loadMissingLayouts();
        else setTokenValid(false);
    }, [token]);

    useEffect(() => {
        setAllFilesSelected(layouts.length > 0 && layouts.every(l => l.file !== null));
    }, [layouts]);

    const loadMissingLayouts = async () => {
        setLoading(true);
        try {
            const resp = await importControlAPI.validateToken({ token });
            if (resp.success && resp.pendings) {
                setTokenValid(true);
                // Ordenação decrescente por mês
                const sorted = resp.pendings.sort((a, b) => b.month.localeCompare(a.month));
                setLayouts(sorted.map(p => ({
                    id: p.import_id,
                    name: p.layout_name,
                    month: p.month,
                    status: 'pending',
                    file: null
                })));
            } else {
                setTokenValid(false);
            }
        } catch (e) {
            setTokenValid(false);
        } finally {
            setLoading(false);
        }
    };

    const isAllowedFileType = (fileName) => {
        const allowedExtensions = ['.xlsx', '.xls', '.csv', '.pdf'];
        const lowerFile = fileName.toLowerCase();
        return allowedExtensions.some(ext => lowerFile.endsWith(ext));
    };

    const handleFileSelect = (id, file) => {
        if (!file && fileUploadRef.current[id]) fileUploadRef.current[id].clear();

        if (file && !isAllowedFileType(file.name)) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Arquivo Inválido',
                detail: 'Apenas arquivos .xlsx, .xls, .csv e .pdf são permitidos.',
                life: 5000
            });
            if (fileUploadRef.current[id]) fileUploadRef.current[id].clear();
            return;
        }

        setLayouts(prev => prev.map(l => l.id === id ? { ...l, file } : l));
    };

    const handleUploadAll = async () => {
        const filesToUpload = layouts.filter(l => l.file);
        if (filesToUpload.length === 0) return;

        setUploading(true);
        setUploadCompleted(false);
        setCurrentUploadIndex(0);
        setTotalUploads(filesToUpload.length);
        setCurrentFileProgress(0);
        setOverallProgress(0);

        try {
            for (let index = 0; index < filesToUpload.length; index++) {
                const item = filesToUpload[index];
                setCurrentUploadIndex(index + 1);

                const formData = new FormData();
                formData.append('token', token);
                formData.append('files', item.file);
                formData.append('import_ids[]', item.id);

                const response = await importControlAPI.upload(formData, {
                    onUploadProgress: (progressEvent) => {
                        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setCurrentFileProgress(percent);
                        setOverallProgress(Math.round(((index + percent / 100) / filesToUpload.length) * 100));
                    }
                });

                if (!response.success) {
                    throw new Error(response.message || 'Erro no upload');
                }
            }

            toast.current.show({ severity: 'success', summary: 'Sucesso', detail: 'Arquivos enviados e logs gerados.' });
            setUploadCompleted(true);
        } catch (error) {
            const backendMessage = error.response?.data?.message || error.message || '';
            const isPdfImageError = error.response?.data?.code === 'PDF_IMAGE_ONLY' || 
                                    backendMessage.toLowerCase().includes('imagem') || 
                                    backendMessage.toLowerCase().includes('incompatível');

            if (isPdfImageError) {
                setFileErrorMessage(backendMessage);
                setShowFileErrorModal(true);
            } else {
                toast.current.show({ 
                    severity: 'error', 
                    summary: 'Erro', 
                    detail: backendMessage || 'Erro no upload' 
                });
            }
        } finally {
            setUploading(false);
            setCurrentFileProgress(0);
        }
    };

    const layoutItem = (layout) => (
        <div key={layout.id} className="p-3 border-1 surface-border border-round mb-3 bg-white shadow-1">
            <div className="flex align-items-center justify-content-between gap-3">
                <div className="flex flex-column gap-1" style={{ width: '130px' }}>
                    <label className="text-xs font-semibold text-500 uppercase">Mês / Ano</label>
                    <Badge value={formatMonthYear(layout.month)} severity="info" className="font-medium w-fit" />
                </div>
                <div className="flex flex-column gap-1 flex-grow-1">
                    <label className="text-xs font-semibold text-500 uppercase">Documento</label>
                    <span className="font-bold text-700">{layout.name}</span>
                </div>
                <div className="flex flex-column gap-1" style={{ width: '110px' }}>
                    <label className="text-xs font-semibold text-500 uppercase">Status</label>
                    <Badge
                        value={(() => {
                            const status = (layout.status ?? '').toString();
                            const normalized = status.trim().toLowerCase();

                            const map = {
                                pending: 'PENDENTE',
                                failed: 'ERRO',
                                error: 'ERRO',
                                erro: 'ERRO',
                                success: 'CONCLUÍDO',
                                ready: 'PRONTO',
                            };

                            return map[normalized] ?? status.toUpperCase();
                        })()}
                        severity={(() => {
                            const status = (layout.status ?? '').toString();
                            const normalized = status.trim().toLowerCase();

                            if (normalized === 'success' || normalized === 'ready') return 'success';
                            if (normalized === 'pending') return 'warning';
                            if (normalized === 'failed' || normalized === 'error' || normalized === 'erro') return 'danger';
                            return 'info';
                        })()}
                        className="w-fit"
                    />
                </div>
                <div className="flex align-items-center ml-2">
                    <FileUpload
                        ref={(el) => fileUploadRef.current[layout.id] = el}
                        mode="basic" chooseLabel="Selecionar Arquivo" chooseIcon="pi pi-plus" className="p-button-sm"
                        auto customUpload uploadHandler={(e) => handleFileSelect(layout.id, e.files[0])}
                        accept=".xlsx,.xls,.csv,.pdf" disabled={uploading || layout.status === 'success'}
                    />
                </div>
            </div>
            {layout.file && (
                <div className="p-2 mt-3 surface-50 border-round border-1 border-200 flex align-items-center justify-content-between border-left-3 border-primary animate-fadein">
                    <div className="flex align-items-center gap-2 overflow-hidden">
                        <i className="pi pi-file-excel text-green-600"></i>
                        <span className="text-xs font-medium truncate">{layout.file.name}</span>
                    </div>
                    <Button icon="pi pi-trash" className="p-button-danger p-button-text p-button-rounded p-button-sm" onClick={() => handleFileSelect(layout.id, null)} disabled={uploading} />
                </div>
            )}
        </div>
    );

    if (loading) {
        return (
            <div className="flex flex-column align-items-center justify-content-center py-8 min-h-screen">
                <ProgressSpinner style={{ width: '50px', height: '50px' }} />
                <p className="mt-3">Carregando dados...</p>
            </div>
        );
    }

    if (tokenValid === false) {
        return (
            <div className="flex flex-column align-items-center justify-content-center py-8 min-h-screen">
                <i className="pi pi-lock text-red-500 text-6xl mb-4"></i>
                <h3>Link de Acesso Inválido</h3>
                <p className="text-500">O token fornecido não é válido ou expirou.</p>
            </div>
        );
    }

    if (uploadCompleted) {
        return (
            <div className="flex flex-column align-items-center justify-content-center py-8 min-h-screen">
                <i className="pi pi-check-circle text-green-500 text-6xl mb-4"></i>
                <h3>Arquivo(s) Enviado(s) com Sucesso!</h3>
                <p className="text-500">Seus arquivos foram recebidos e estão sendo processados.</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-4">
            <Toast ref={toast} />
            <Card className="shadow-2 border-round-xl overflow-hidden" title="Pendências de Importação" subTitle="Envie os arquivos correspondentes para cada mês/ano listado abaixo">
                <div className="max-h-[70vh] overflow-auto px-2">
                    {layouts.length === 0 ? (
                        <div className="text-center py-8 text-500">Nenhuma pendência encontrada.</div>
                    ) : (
                        layouts.map(layoutItem)
                    )}
                </div>
                <div className="flex flex-column w-full gap-3 mt-4">
                    {uploading && (
                        <div className="w-full px-2">
                            <div className="flex align-items-center justify-content-between mb-2">
                                <div>
                                    <strong>Arquivo {currentUploadIndex} de {totalUploads}</strong>
                                    <div className="text-sm text-500">{layouts[currentUploadIndex - 1]?.name || 'Preparando...'}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-sm text-500">Progresso do arquivo</div>
                                    <strong>{currentFileProgress}%</strong>
                                </div>
                            </div>
                            <ProgressBar value={currentFileProgress} style={{ height: '8px' }} showValue={false} className="mb-2" />
                            <div className="flex align-items-center justify-content-between mb-2">
                                <div className="text-sm text-500">Progresso total</div>
                                <strong>{overallProgress}%</strong>
                            </div>
                            <ProgressBar value={overallProgress} style={{ height: '8px' }} showValue={false} />
                        </div>
                    )}

                    <div className="flex align-items-center justify-content-between w-full">
                        <div className="flex align-items-center gap-3 surface-50 p-2 border-round border-1 border-200 shadow-1">
                            <div className="flex flex-column px-2">
                                <span className="text-xs text-500 font-bold uppercase" style={{ fontSize: '10px' }}>Pendentes</span>
                                <span className="text-lg font-bold text-700 text-center line-height-1">{layouts.length}</span>
                            </div>
                            <div className="border-left-1 border-300" style={{ height: '20px' }}></div>
                            <div className="flex flex-column px-2">
                                <span className="text-xs text-500 font-bold uppercase" style={{ fontSize: '10px' }}>Adicionados</span>
                                <span className={`text-lg font-bold text-center line-height-1 ${layouts.filter(l => l.file).length === layouts.length ? 'text-green-600' : 'text-primary'}`}>
                                    {layouts.filter(l => l.file).length}
                                </span>
                            </div>
                        </div>

                        <Button label="Enviar Arquivos" icon="pi pi-upload" onClick={handleUploadAll} loading={uploading} disabled={layouts.filter(l => l.file).length == 0} className="p-button-success" />
                    </div>
                </div>
            </Card>

            <FileErrorModal
                visible={showFileErrorModal}
                onHide={() => setShowFileErrorModal(false)}
                errorDetails={fileErrorMessage}
            />
        </div>
    );
};

export default MissingLayoutsUploadPage;